import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useRoute, useNavigation } from '@react-navigation/native';
import type { StackScreenProps } from '@react-navigation/stack';
import type { RootStackParamList } from '@/navigation/RootNavigator';
import { UnifiedModal } from '@/components/common/UnifiedModal';
import { useToast } from '@/contexts/admin/ToastContext';
import { extractErrorMessage } from '@/utils/common/errorHandling';
import { useAppSelector } from '@/store';
import {
  useGetPaymentMethodsQuery,
  useGetFeeStructureQuery,
  usePostPayBalanceMutation,
} from '@/store/api';
import { useLeaseAutoPay } from '@/features/payments/hooks/useLeaseAutoPay';
import { usePaymentBalance } from '@/features/payments/api/paymentHooks';
import { calculatePaymentFees, getPaymentMethodShortLabel } from '@/utils/residents/paymentUtils';
import { parsePaymentResponse } from '@/utils/residents/payment/parseResponse';
import { formatCurrency } from '@/utils/common/formatting';
import type { PaymentMethod, FeeStructure } from '@/types/residents/payment';
import { useAmountInput } from '@/features/payments/hooks/useAmountInput';

// Import shared components
import {
  PaymentModalFooter,
  InfoBubbleBottomSheet,
} from '@/components/residents/payments/mobile/makePayment';

// Import content components for each screen
import { PaymentDetailsContent } from './content/PaymentDetailsContent';
import { AddPaymentMethodContent } from './content/AddPaymentMethodContent';
import { ChangePaymentMethodContent } from './content/ChangePaymentMethodContent';
import { ConfirmPaymentContent } from './content/ConfirmPaymentContent';
import { CreditCardFormContent } from './content/CreditCardFormContent';
import { ACHFormContent } from './content/ACHFormContent';
import { TransactionProcessingContent } from './content/TransactionProcessingContent';
import { TransactionReceivedContent } from './content/TransactionReceivedContent';
import { ManageWalletContent } from './content/ManageWalletContent';
import { MobileAutopayDetailsModal } from './MobileAutopayDetailsModal';

// Screen types for the payment flow
export type PaymentScreen = 
  | 'details' 
  | 'add' 
  | 'change' 
  | 'confirm' 
  | 'cardForm' 
  | 'achForm' 
  | 'processing' 
  | 'success'
  | 'manageWallet';

// Screen configuration for titles
const SCREEN_TITLES: Record<PaymentScreen, string> = {
  details: 'Payment details',
  add: 'Add a payment method',
  change: 'Change payment method',
  confirm: '', // No title for confirm screen
  cardForm: 'Credit or debit card details',
  achForm: 'ACH details',
  processing: '', // No title for processing
  success: 'Transaction received',
  manageWallet: 'Manage my wallet',
};

type MobileMakePaymentScreenProps = StackScreenProps<RootStackParamList, 'MakePayment'>;

/**
 * Mobile Make Payment Screen - Unified Single Modal Architecture
 * 
 * Performance optimizations:
 * 1. Single Modal that stays mounted throughout the entire flow
 * 2. Content swapping instead of Modal mounting/unmounting
 * 3. Screen history stack for smooth back navigation
 * 4. All content components are lightweight and share the same container
 */
export const MobileMakePaymentScreen: React.FC = () => {
  const route = useRoute<MobileMakePaymentScreenProps['route']>();
  const navigation = useNavigation<MobileMakePaymentScreenProps['navigation']>();
  const { showError } = useToast();

  const { leaseId } = route.params;

  // Redux state
  const authUser = useAppSelector((s) => (s as any).auth?.user);
  const authUserId = authUser?.id;
  const payingForResidentId = useAppSelector((s) => (s as any).payments?.payingForResidentId);
  const associatedLeaseAccount = useAppSelector((s) => (s as any).payments?.associatedLeaseAccount);
  const residentId = payingForResidentId || authUserId;

  // API queries
  const { balanceDue, balanceItems, accountCredit } = usePaymentBalance(leaseId);
  const { data: methodsData, isLoading: loadingMethods, refetch: refetchMethods } = useGetPaymentMethodsQuery();
  const { data: feeData } = useGetFeeStructureQuery();
  const { hasAutoPay: hasAutoPaySetUp, isLoading: isLoadingLease } = useLeaseAutoPay(leaseId);
  const [makePayment] = usePostPayBalanceMutation();

  // Screen navigation state - uses a history stack for back navigation
  const [screenHistory, setScreenHistory] = useState<PaymentScreen[]>(['details']);
  const currentScreen = screenHistory[screenHistory.length - 1];
  
  // Payment form state
  const [selectedMethodId, setSelectedMethodId] = useState<number | null>(null);
  const [amountError, setAmountError] = useState<string | undefined>();
  const {
    amountType: selectedAmountOption,
    customAmount,
    inputRef: amountInputRef,
    selectBalance: handleSelectBalance,
    selectOther: handleSelectOther,
    handleAmountChange: handleCustomAmountChange,
  } = useAmountInput({ onAmountChange: () => setAmountError(undefined) });
  const [methodError, setMethodError] = useState<string | undefined>();
  const [showInfoBubble, setShowInfoBubble] = useState(false);

  // Transaction result state
  const [transactionDetails, setTransactionDetails] = useState<{
    totalCharged: number;
    fee: number;
    paymentMethodLabel: string;
    paymentId?: number;
    retref?: string;
    authcode?: string;
  } | null>(null);

  // Autopay setup modal state
  const [showAutopaySetupModal, setShowAutopaySetupModal] = useState(false);

  // Helper to parse expiry string
  const parseExpiry = (expiry: string | undefined): { month?: number; year?: number } => {
    if (!expiry) return {};
    let mm: number, yy: number;
    if (expiry.includes('/')) {
      const [month, year] = expiry.split('/');
      mm = Number(month);
      yy = Number(year);
    } else if (expiry.length === 4) {
      mm = Number(expiry.substring(0, 2));
      yy = Number(expiry.substring(2));
    } else {
      return {};
    }
    const fullYear = yy < 100 ? 2000 + yy : yy;
    return { month: mm, year: fullYear };
  };

  // Derived data
  const paymentMethods: PaymentMethod[] = useMemo(() => {
    const methods = methodsData?.response || [];
    return methods.map((m: any) => {
      const parsedExpiry = parseExpiry(m.expiry);
      return {
        id: m.id,
        sourceId: m.sourceId || m.id,
        type: m.type === 'card' ? 'card' : 'ach',
        name: m.name,
        last4: m.last4,
        brand: m.brand || m.accttype,
        bankName: m.bankName || m.name,
        isExpired: m.isExpired,
        expiryMonth: m.expiryMonth || parsedExpiry.month,
        expiryYear: m.expiryYear || parsedExpiry.year,
        isDefault: m.isDefault,
      };
    });
  }, [methodsData]);

  const selectedPaymentMethod = useMemo(() => {
    if (!selectedMethodId) return null;
    return paymentMethods.find((m) => {
      const methodSourceId = Number(m.sourceId) || m.id;
      return methodSourceId === selectedMethodId;
    }) || null;
  }, [selectedMethodId, paymentMethods]);

  const feeStructure: FeeStructure = useMemo(() => {
    const fees = feeData?.response || {};
    return {
      creditCard: fees.creditCard || '3.39%',
      debitCard: fees.debitCard || '2%',
      bankAccount: fees.bankAccount || '$3.99',
    };
  }, [feeData]);

  // Payment calculations
  const paymentAmount = useMemo(() => {
    if (selectedAmountOption === 'balance') return balanceDue;
    return parseFloat(customAmount) || 0;
  }, [selectedAmountOption, customAmount, balanceDue]);

  const feeCalculation = useMemo(() => {
    if (!selectedPaymentMethod || !paymentAmount) {
      return { fee: 0, total: paymentAmount };
    }
    const normalizedFeeStructure = {
      creditCard: feeStructure.creditCard || '3.39%',
      debitCard: feeStructure.debitCard || '2%',
      bankAccount: feeStructure.bankAccount || '$3.99',
    };
    return calculatePaymentFees(paymentAmount, selectedPaymentMethod, normalizedFeeStructure);
  }, [paymentAmount, selectedPaymentMethod, feeStructure]);

  const calculatedFee = feeCalculation.fee;
  const totalAmount = feeCalculation.total;

  // Get resident name for display
  const residentName = useMemo(() => {
    if (associatedLeaseAccount) {
      const { firstName, lastName } = associatedLeaseAccount;
      if (firstName && lastName) return `${firstName} ${lastName.charAt(0)}.`;
    }
    return undefined;
  }, [associatedLeaseAccount]);

  // Set default payment method on load
  useEffect(() => {
    if (paymentMethods.length > 0 && !selectedMethodId) {
      const defaultMethod = paymentMethods.find((m) => m.isDefault) || paymentMethods[0];
      if (defaultMethod) {
        const sourceId = Number(defaultMethod.sourceId) || Number(defaultMethod.id);
        setSelectedMethodId(sourceId);
      }
    }
  }, [paymentMethods, selectedMethodId]);

  // Navigation helpers - no Modal unmount/mount, just content swap
  const navigateTo = useCallback((screen: PaymentScreen) => {
    setScreenHistory(prev => [...prev, screen]);
  }, []);

  const goBack = useCallback(() => {
    if (screenHistory.length > 1) {
      setScreenHistory(prev => prev.slice(0, -1));
    } else if (navigation.canGoBack()) {
      navigation.goBack();
    }
  }, [screenHistory, navigation]);

  const resetToDetails = useCallback(() => {
    setScreenHistory(['details']);
  }, []);

  // Close the entire modal
  const handleClose = useCallback(() => {
    if (navigation.canGoBack()) {
      navigation.goBack();
    }
  }, [navigation]);

  // Payment handlers
  const handleAddPaymentMethod = useCallback(() => {
    navigateTo('add');
  }, [navigateTo]);

  const handleChangePaymentMethod = useCallback(() => {
    navigateTo('change');
  }, [navigateTo]);

  const handlePaymentMethodAdded = useCallback((newMethodId?: number) => {
    refetchMethods();
    if (newMethodId) setSelectedMethodId(newMethodId);
    setMethodError(undefined);
    resetToDetails();
  }, [refetchMethods, resetToDetails]);

  const handlePaymentMethodSelected = useCallback((methodId: number) => {
    setSelectedMethodId(methodId);
    setMethodError(undefined);
    goBack();
  }, [goBack]);

  const MINIMUM_PAYMENT_AMOUNT = 10;

  const validateAndProceed = useCallback(() => {
    let hasError = false;

    if (paymentAmount <= 0) {
      setAmountError('Please enter a valid payment amount');
      hasError = true;
    } else if (paymentAmount < MINIMUM_PAYMENT_AMOUNT) {
      setAmountError(`Minimum payment amount is ${formatCurrency(MINIMUM_PAYMENT_AMOUNT)}`);
      hasError = true;
    }

    if (!selectedPaymentMethod) {
      setMethodError('Please add a payment method');
      hasError = true;
    } else if (selectedPaymentMethod.isExpired) {
      setMethodError('Please add a payment method');
      hasError = true;
    }

    if (hasError) return;
    navigateTo('confirm');
  }, [paymentAmount, selectedPaymentMethod, navigateTo]);

  const handleConfirmPayment = useCallback(async () => {
    if (!selectedPaymentMethod) return;

    navigateTo('processing');

    try {
      const sourceId = Number(selectedPaymentMethod.sourceId) || Number(selectedPaymentMethod.id);

      const result = await makePayment({
        amount: paymentAmount,
        leaseId,
        sourceId,
        residentId,
      }).unwrap();

      const parsed = parsePaymentResponse(result);
      if (!parsed.approved) {
        throw new Error(parsed.errorMessage);
      }

      setTransactionDetails({
        totalCharged: parsed.amount ?? totalAmount,
        fee: calculatedFee,
        paymentMethodLabel: getPaymentMethodShortLabel(selectedPaymentMethod),
        paymentId: parsed.paymentId,
        retref: parsed.retref,
        authcode: parsed.authcode,
      });

      navigateTo('success');
    } catch (error: any) {
      showError(extractErrorMessage(error, 'Payment failed. Please try again.'));
      goBack(); // Go back to confirm screen
    }
  }, [selectedPaymentMethod, paymentAmount, leaseId, residentId, makePayment, totalAmount, calculatedFee, navigateTo, showError, goBack]);

  const handleSuccessClose = useCallback(() => {
    handleClose();
  }, [handleClose]);

  const handleSetupAutopay = useCallback(() => {
    // Don't call handleClose() here - we need this component to stay mounted
    // so the autopay modal inside it can be shown
    setShowAutopaySetupModal(true);
  }, []);

  // Info bubble message
  const infoBubbleMessage = `If the amount is more than the balance due, the overpaid amount will appear as a credit to the user's account and will be applied towards future charges when they are issued.`;

  // Determine if we should show header
  const showHeader = !['processing'].includes(currentScreen);
  const currentTitle = SCREEN_TITLES[currentScreen];

  // Render the current screen content
  const renderContent = () => {
    switch (currentScreen) {
      case 'details':
        return (
          <PaymentDetailsContent
            balanceDue={balanceDue}
            balanceItems={balanceItems}
            accountCredit={accountCredit}
            selectedAmountOption={selectedAmountOption}
            customAmount={customAmount}
            onSelectBalance={handleSelectBalance}
            onSelectOther={handleSelectOther}
            onCustomAmountChange={handleCustomAmountChange}
            onInfoPress={() => setShowInfoBubble(true)}
            residentName={residentName}
            disabled={false}
            amountError={amountError}
            paymentMethod={selectedPaymentMethod}
            onAddPaymentMethod={handleAddPaymentMethod}
            onChangePaymentMethod={handleChangePaymentMethod}
            methodError={methodError}
            feeStructure={feeStructure}
            inputRef={amountInputRef}
          />
        );

      case 'add':
        return (
          <AddPaymentMethodContent
            onSelectCard={() => navigateTo('cardForm')}
            onSelectACH={() => navigateTo('achForm')}
            feeStructure={feeStructure}
          />
        );

      case 'change':
        return (
          <ChangePaymentMethodContent
            paymentMethods={paymentMethods}
            selectedMethodId={selectedMethodId}
            onMethodSelected={handlePaymentMethodSelected}
            onAddCard={() => navigateTo('cardForm')}
            onAddACH={() => navigateTo('achForm')}
            onManageWallet={() => navigateTo('manageWallet')}
            feeStructure={feeStructure}
          />
        );

      case 'cardForm':
        return (
          <CreditCardFormContent
            onCardAdded={handlePaymentMethodAdded}
            onBack={goBack}
          />
        );

      case 'achForm':
        return (
          <ACHFormContent
            onACHAdded={handlePaymentMethodAdded}
            onBack={goBack}
          />
        );

      case 'confirm':
        return (
          <ConfirmPaymentContent
            paymentAmount={paymentAmount}
            fee={calculatedFee}
            totalAmount={totalAmount}
            paymentMethod={selectedPaymentMethod}
            residentName={residentName}
          />
        );

      case 'processing':
        return <TransactionProcessingContent />;

      case 'success':
        return (
          <TransactionReceivedContent
            paymentAmount={paymentAmount}
            fee={transactionDetails?.fee || 0}
            totalCharged={transactionDetails?.totalCharged || 0}
            paymentMethodLabel={transactionDetails?.paymentMethodLabel || ''}
            residentName={residentName}
            paymentId={transactionDetails?.paymentId}
            retref={transactionDetails?.retref}
            authcode={transactionDetails?.authcode}
          />
        );

      case 'manageWallet':
        return (
          <ManageWalletContent
            paymentMethods={paymentMethods}
            onMethodsUpdated={() => {
              refetchMethods();
              goBack();
            }}
          />
        );

      default:
        return null;
    }
  };

  // Render footer based on current screen
  const renderFooter = () => {
    switch (currentScreen) {
      case 'details':
        return (
          <PaymentModalFooter
            onCancel={handleClose}
            onProceed={validateAndProceed}
            cancelText="Cancel"
            proceedText="Proceed"
            proceedDisabled={loadingMethods}
          />
        );

      case 'add':
        return (
          <PaymentModalFooter
            onCancel={goBack}
            onProceed={goBack}
            cancelText="Cancel"
            proceedText="Cancel"
            showCancel={false}
            variant="single"
          />
        );

      case 'change':
        return (
          <PaymentModalFooter
            onCancel={goBack}
            onProceed={goBack}
            cancelText="Cancel"
            proceedText="Save"
          />
        );

      case 'cardForm':
      case 'achForm':
        // Footer is handled inside the content component for form submission
        return null;

      case 'confirm':
        return (
          <PaymentModalFooter
            onCancel={goBack}
            onProceed={handleConfirmPayment}
            cancelText="No, cancel"
            proceedText="Yes, I'm sure"
          />
        );

      case 'success':
        if (isLoadingLease) return null;
        return hasAutoPaySetUp ? (
          <PaymentModalFooter
            onCancel={handleSuccessClose}
            onProceed={handleSuccessClose}
            proceedText="Done"
            showCancel={false}
            variant="single"
          />
        ) : (
          <PaymentModalFooter
            onCancel={handleSuccessClose}
            onProceed={handleSetupAutopay}
            cancelText="No, thanks, I'm finished"
            proceedText="Set up a recurring payment"
            variant="autopay"
          />
        );

      case 'manageWallet':
        return (
          <PaymentModalFooter
            onCancel={goBack}
            onProceed={goBack}
            proceedText="Done"
            showCancel={false}
            variant="single"
          />
        );

      default:
        return null;
    }
  };

  const footerContent = renderFooter();
  const hasEmbeddedFooter = currentScreen === 'cardForm' || currentScreen === 'achForm';
  const isProcessing = currentScreen === 'processing';

  return (
    <>
      <UnifiedModal
        visible={true}
        variant="fullscreen"
        animateEntry={false}
        onClose={handleClose}
        onRequestClose={isProcessing ? undefined : handleClose}
        title={currentTitle || undefined}
        centerTitle
        showHeader={showHeader}
        footer={hasEmbeddedFooter ? undefined : footerContent}
        footerStyle={hasEmbeddedFooter ? undefined : footerPaddingReset}
        avoidKeyboard
        scrollable={!isProcessing}
        contentContainerStyle={isProcessing ? processingContentStyle : scrollContentOverride}
      >
        {renderContent()}
      </UnifiedModal>

      <InfoBubbleBottomSheet
        visible={showInfoBubble}
        onClose={() => setShowInfoBubble(false)}
        message={infoBubbleMessage}
      />

      <MobileAutopayDetailsModal
        visible={showAutopaySetupModal}
        onClose={() => {
          setShowAutopaySetupModal(false);
          handleClose();
        }}
        leaseId={leaseId}
        onAutopayUpdated={() => {
          setShowAutopaySetupModal(false);
          handleClose();
        }}
        mode="setup"
      />
    </>
  );
};

const footerPaddingReset = { paddingHorizontal: 0, paddingTop: 0, paddingBottom: 0 };
const scrollContentOverride = { paddingHorizontal: 0, paddingBottom: 0, flexGrow: 1 };
const processingContentStyle = { justifyContent: 'center' as const, alignItems: 'center' as const, flexGrow: 1 };

export default MobileMakePaymentScreen;
